import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { CaseDocumentFileModel } from "@shared/models";
import { bytesToMegaBytes } from "@shared/utils";
import { CASE_DOCUMENT_LIMITS, CaseDocumentLimits, UploadedCaseFileKind } from "@containers/Cases/constants";
import { CheckedCaseFile } from "@containers/Cases/interface";
import { deleteCaseFile, discardTemporaryCaseFiles, uploadCaseFile } from "@containers/Cases/store/actions";
import { UPLOAD_FAILED_MESSAGE } from "@containers/Cases/utils/caseDocuments";

export enum UPLOAD_STATUS {
  QUEUED = "queued",
  UPLOADING = "uploading",
  READY = "ready",
  FAILED = "failed",
}

export interface UploadRow {
  key: string;
  file: File;
  /** Empty for a format the document doesn't accept */
  mimeType: string;
  status: UPLOAD_STATUS;
  /** Temporary case file id, once uploaded */
  id: number | null;
  pages: number | null;
  error: string | null;
}

export interface UploadTotals {
  files: number;
  size: number;
  pages: number;
}

const getExtension = (fileName: string) => fileName.split(".").pop()?.toLowerCase() ?? "";

const sum = (values: (number | null)[]) => values.reduce<number>((total, value) => total + (value ?? 0), 0);

const getTotals = (existingFiles: CaseDocumentFileModel[], rows: UploadRow[]): UploadTotals => {
  const countedRows = rows.filter(({ status }) => status !== UPLOAD_STATUS.FAILED);

  return {
    files: existingFiles.length + countedRows.length,
    size: sum(existingFiles.map(({ size }) => size)) + sum(countedRows.map(({ file }) => file.size)),
    pages: sum(existingFiles.map(({ pages }) => pages)) + sum(countedRows.map(({ pages }) => pages)),
  };
};

/** The problems found before uploading. Pages are counted by the server after the upload. */
const getUploadProblem = (file: File, limits: CaseDocumentLimits): string | null => {
  const extension = getExtension(file.name);

  if (limits.formatErrors?.[extension]) {
    return limits.formatErrors[extension];
  }
  if (!limits.formats[extension]) {
    const formats = `${limits.formatNames.slice(0, -1).join(", ")} or ${limits.formatNames.at(-1)}`;
    return `This format isn't supported. Upload ${formats}.`;
  }
  if (file.size > limits.maxFileSize) {
    return `This file is over ${bytesToMegaBytes(limits.maxFileSize)} MB.`;
  }
  return null;
};

const getLimitError = ({ maxPages, maxFiles, maxTotalSize }: CaseDocumentLimits, totals: UploadTotals) => {
  if (maxPages && totals.pages > maxPages) return `${totals.pages} pages selected. The limit is ${maxPages}.`;
  if (totals.files > maxFiles) return `${totals.files} files selected. The limit is ${maxFiles}.`;
  if (totals.size > maxTotalSize) {
    return `${Number(bytesToMegaBytes(totals.size).toFixed(1))} MB selected. The limit is ${bytesToMegaBytes(maxTotalSize)} MB.`;
  }
  return null;
};

const toQueuedRow = (key: string, file: File, limits: CaseDocumentLimits): UploadRow => {
  const error = getUploadProblem(file, limits);

  return {
    key,
    file,
    mimeType: limits.formats[getExtension(file.name)] ?? "",
    status: error ? UPLOAD_STATUS.FAILED : UPLOAD_STATUS.QUEUED,
    id: null,
    pages: null,
    error,
  };
};

/**
 * Uploads files to a case document one at a time as temporary files, checking them before and after.
 * Temporary files left behind are deleted when the uploads start and end, e.g. when the window closes.
 * When replacing, the document's files can be removed too; they are deleted once the document is parsed.
 */
const useCaseDocumentUpload = (
  caseId: number,
  kind: UploadedCaseFileKind,
  documentFiles: CaseDocumentFileModel[],
  replace = false,
) => {
  const dispatch = useDispatch();
  const limits: CaseDocumentLimits = CASE_DOCUMENT_LIMITS[kind];
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [replacedIds, setReplacedIds] = useState<number[]>([]);
  // The upload callbacks run after renders they don't see, so they read the rows from here.
  const rowsRef = useRef<UploadRow[]>([]);

  const commitRows = useCallback((nextRows: UploadRow[]) => {
    rowsRef.current = nextRows;
    setRows(nextRows);
  }, []);

  const updateRow = useCallback(
    (key: string, changes: Partial<UploadRow>) => {
      commitRows(rowsRef.current.map((row) => (row.key === key ? { ...row, ...changes } : row)));
    },
    [commitRows],
  );

  const existingFiles = useMemo(
    () => documentFiles.filter(({ id }) => !replacedIds.includes(id)),
    [documentFiles, replacedIds],
  );

  const uploadFile = useCallback(
    (file: File) =>
      new Promise<CheckedCaseFile | undefined>((resolve) => {
        dispatch(uploadCaseFile({ caseId, kind, file, replace, cb: resolve }));
      }),
    [caseId, dispatch, kind, replace],
  );

  const uploadRow = useCallback(
    async ({ key, file }: UploadRow) => {
      updateRow(key, { status: UPLOAD_STATUS.UPLOADING });

      const checkedFile = await uploadFile(file);

      updateRow(
        key,
        !checkedFile || checkedFile.error
          ? {
              status: UPLOAD_STATUS.FAILED,
              pages: checkedFile?.pages ?? null,
              error: checkedFile?.error ?? UPLOAD_FAILED_MESSAGE,
            }
          : { status: UPLOAD_STATUS.READY, id: checkedFile.id, pages: checkedFile.pages },
      );
    },
    [updateRow, uploadFile],
  );

  const uploadedFiles =
    existingFiles.length +
    rows.filter(({ status }) => status === UPLOAD_STATUS.UPLOADING || status === UPLOAD_STATUS.READY).length;
  const nextRow = rows.some(({ status }) => status === UPLOAD_STATUS.UPLOADING)
    ? undefined
    : rows.find(({ status }) => status === UPLOAD_STATUS.QUEUED);
  // Files over the file limit wait in the queue, so that removing others lets them upload.
  const canUploadNext = uploadedFiles < limits.maxFiles;

  useEffect(() => {
    if (nextRow && canUploadNext) uploadRow(nextRow);
  }, [nextRow, canUploadNext, uploadRow]);

  const addFiles = useCallback(
    (files: File[]) => {
      commitRows([...rowsRef.current, ...files.map((file) => toQueuedRow(crypto.randomUUID(), file, limits))]);
    },
    [commitRows, limits],
  );

  const deleteUpload = useCallback(
    ({ id }: UploadRow) => {
      if (id) dispatch(deleteCaseFile.request({ caseId, fileId: id }));
    },
    [caseId, dispatch],
  );

  const removeRow = useCallback(
    (row: UploadRow) => {
      deleteUpload(row);
      commitRows(rowsRef.current.filter(({ key }) => key !== row.key));
    },
    [commitRows, deleteUpload],
  );

  const replaceRow = useCallback(
    (row: UploadRow, file: File) => {
      deleteUpload(row);
      commitRows(rowsRef.current.map((item) => (item.key === row.key ? toQueuedRow(row.key, file, limits) : item)));
    },
    [commitRows, deleteUpload, limits],
  );

  useEffect(() => {
    const discardUploads = () => {
      dispatch(discardTemporaryCaseFiles({ caseId, kind }));
    };

    discardUploads();
    return () => {
      // Stops the queue; the file being uploaded is left to the cleanup on the next open or after a day.
      rowsRef.current = [];
      discardUploads();
    };
  }, [caseId, dispatch, kind]);

  const removeExistingFile = useCallback((fileId: number) => {
    setReplacedIds((ids) => [...ids, fileId]);
  }, []);

  const readyIds = useMemo(
    () => rows.flatMap(({ status, id }) => (status === UPLOAD_STATUS.READY && id ? [id] : [])),
    [rows],
  );

  const totals = getTotals(existingFiles, rows);
  const isUploading = rows.some(({ status }) => status === UPLOAD_STATUS.QUEUED || status === UPLOAD_STATUS.UPLOADING);
  // Queued files and the kept files of a replacement aren't checked by the server until later, so they can be over.
  const limitError = getLimitError(limits, totals);
  const hasChanges = readyIds.length > 0 || (replacedIds.length > 0 && existingFiles.length > 0);

  return {
    rows,
    totals,
    readyIds,
    existingFiles,
    replacedIds,
    limitError,
    isUploading,
    canParse: hasChanges && !isUploading && !limitError,
    addFiles,
    replaceRow,
    removeRow,
    removeExistingFile,
  };
};

export default useCaseDocumentUpload;
